<template>
  <div class="publicform-overview">
    <v-skeleton-loader v-if="loading" type="card, list-item-three-line, list-item-three-line" />

    <v-alert v-else-if="!allowedDisplay" type="error" outlined>
      {{ datas.message || 'Le formulaire ne peut pas être affiché.' }}
    </v-alert>

    <template v-else>
      <div class="overview-header pa-5 mb-5">
        <div class="d-flex flex-wrap align-center">
          <div>
            <div class="text-overline">Résumé du formulaire public</div>
            <h2>{{ formGeneral.title || datas.module.name || 'Formulaire sans titre' }}</h2>
            <div class="mt-1 overview-description">
              {{ formGeneral.description || datas.module.description || 'Aucune description renseignée.' }}
            </div>
          </div>
          <v-spacer />
          <v-chip :color="datas.form.wizard.enabled ? 'success' : 'grey'" dark>
            <v-icon left small>{{ datas.form.wizard.enabled ? 'mdi-check-circle' : 'mdi-pause-circle' }}</v-icon>
            {{ datas.form.wizard.enabled ? 'Parcours actif' : 'Parcours désactivé' }}
          </v-chip>
        </div>
      </div>

      <v-row dense>
        <v-col v-for="stat in summaryStats" :key="stat.label" cols="6" sm="4" lg="2">
          <v-card outlined class="overview-stat pa-3">
            <v-icon :color="stat.color" class="mb-2">{{ stat.icon }}</v-icon>
            <div class="text-h5 font-weight-bold">{{ stat.value }}</div>
            <div class="text-caption text--secondary">{{ stat.label }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-alert v-if="datas.summary.warnings.length" type="warning" outlined class="mt-5 mb-3">
        <div class="font-weight-bold mb-1">Points à vérifier</div>
        <div v-for="(warning, index) in datas.summary.warnings" :key="`warning-${index}`">
          • {{ warning }}
        </div>
      </v-alert>

      <v-row class="mt-2">
        <v-col cols="12" lg="8">
          <div class="d-flex align-center mb-3">
            <v-icon color="primary" class="mr-2">mdi-format-list-numbered</v-icon>
            <h3>Parcours du formulaire</h3>
          </div>

          <v-alert v-if="!datas.summary.steps.length" type="info" text>Aucune étape n'est configurée.</v-alert>

          <v-card
            v-for="(step, stepIndex) in datas.summary.steps"
            :key="step.key || `step-${stepIndex}`"
            outlined
            class="mb-3 overview-step"
          >
            <v-card-title class="py-3">
              <v-avatar color="primary" size="30" class="mr-3 white--text">{{ stepIndex + 1 }}</v-avatar>
              <div>
                <div class="text-subtitle-1 font-weight-bold">{{ step.title }}</div>
                <div v-if="step.description" class="text-caption text--secondary">{{ step.description }}</div>
              </div>
              <v-spacer />
              <v-chip small outlined>{{ step.items.length }} élément{{ step.items.length > 1 ? 's' : '' }}</v-chip>
            </v-card-title>
            <v-divider />

            <v-card-text v-if="step.items.length" class="py-2">
              <div
                v-for="(item, itemIndex) in step.items"
                :key="`${step.key}-${itemIndex}`"
                class="overview-item d-flex align-center py-2"
              >
                <v-avatar :color="itemKindColor(item.kind)" size="30" class="mr-3">
                  <v-icon dark small>{{ itemKindIcon(item.kind) }}</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-medium">{{ item.title || item.key || 'Élément sans titre' }}</div>
                  <div class="text-caption text--secondary">
                    {{ itemKindLabel(item.kind) }}
                    <span v-if="item.usage"> · {{ item.usage === 'insert' ? 'enregistré' : 'visuel uniquement' }}</span>
                    <span v-if="item.renderAs"> · {{ item.renderAs }}</span>
                    <span v-if="item.conditions"> · {{ item.conditions }} condition(s)</span>
                  </div>
                </div>
                <v-chip v-if="item.required" x-small color="error" outlined>Obligatoire</v-chip>
                <v-chip x-small class="ml-2" outlined>{{ item.width }}</v-chip>
              </div>
            </v-card-text>
            <v-card-text v-else class="text--secondary">Cette étape est vide.</v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="4">
          <v-card outlined class="mb-4">
            <v-card-title class="text-subtitle-1">
              <v-icon color="indigo" class="mr-2">mdi-database-search</v-icon>Requêtes
            </v-card-title>
            <v-divider />
            <v-list v-if="datas.form.queries.length" dense>
              <v-list-item v-for="(query, index) in datas.form.queries" :key="query.key || `query-${index}`">
                <v-list-item-content>
                  <v-list-item-title>{{ query.title || query.key || `Requête ${index + 1}` }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ query.table || 'Table non renseignée' }} · {{ queryFieldCount(query) }} champ(s)
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-chip v-if="query.stopifdoublon" x-small color="warning" outlined>Doublon</v-chip>
              </v-list-item>
            </v-list>
            <v-card-text v-else class="text--secondary">Aucune requête configurée.</v-card-text>
          </v-card>

          <v-card outlined>
            <v-card-title class="text-subtitle-1">
              <v-icon color="teal" class="mr-2">mdi-lock-outline</v-icon>Valeurs fixes
            </v-card-title>
            <v-divider />
            <v-list v-if="datas.form.defaults.length" dense>
              <v-list-item v-for="(field, index) in datas.form.defaults" :key="`${field.key}-${index}`">
                <v-list-item-content>
                  <v-list-item-title>{{ field.key || 'Champ non renseigné' }}</v-list-item-title>
                  <v-list-item-subtitle>{{ displayValue(field.value) }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-card-text v-else class="text--secondary">Aucune valeur fixe configurée.</v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script>
import { client } from '../mixins';

export default {
  name: 'PublicformClient',
  mixins: [client],

  data() {
    return { datas: {}, loading: true, allowedDisplay: true };
  },

  computed: {
    formGeneral() {
      return this.datas.form?.general || {};
    },
    summaryStats() {
      const counts = this.datas.summary?.counts || {};
      return [
        { label: 'Étapes', value: counts.steps || 0, icon: 'mdi-format-list-numbered', color: 'primary' },
        { label: 'Éléments', value: counts.items || 0, icon: 'mdi-view-grid-outline', color: 'blue' },
        { label: 'Champs', value: counts.fields || 0, icon: 'mdi-form-textbox', color: 'indigo' },
        { label: 'Virtuels', value: counts.virtualFields || 0, icon: 'mdi-tune-variant', color: 'deep-purple' },
        { label: 'Listes', value: counts.queryLists || 0, icon: 'mdi-format-list-checks', color: 'teal' },
        { label: 'Obligatoires', value: counts.required || 0, icon: 'mdi-alert-circle-outline', color: 'error' }
      ];
    }
  },

  methods: {
    callAPI() {
      this.loading = true;
      this.API({
        type: 'post',
        url: 'displaymodule',
        params: { id: this.idmodule, params: this.params, querys: this.$route.query }
      }).then((resp) => this.manageDatas(resp.data))
        .catch(() => this.manageDatas({ success: false, message: 'Impossible de charger le résumé du formulaire.' }));
    },
    manageDatas(data) {
      this.datas = data || {};
      this.allowedDisplay = Boolean(data?.success && data?.form && data?.summary);
      this.loading = false;
    },
    itemKindIcon(kind) {
      return { content: 'mdi-text-box-outline', field: 'mdi-form-textbox', virtual: 'mdi-tune-variant', queryList: 'mdi-format-list-checks' }[kind] || 'mdi-help-circle-outline';
    },
    itemKindColor(kind) {
      return { content: 'blue-grey', field: 'primary', virtual: 'deep-purple', queryList: 'teal' }[kind] || 'grey';
    },
    itemKindLabel(kind) {
      return { content: 'Texte', field: 'Champ de la table', virtual: 'Champ virtuel', queryList: 'Liste issue d’une requête' }[kind] || kind;
    },
    queryFieldCount(query) {
      return Array.isArray(query.champs) ? query.champs.length : 0;
    },
    displayValue(value) {
      if (value === null || typeof value === 'undefined' || value === '') return 'Valeur vide';
      return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  },

  mounted() {
    this.params = this.$store.getters.specificPage(this.modalKey).params;
    const fields = this.$store.getters.listForm(this.modalKey)?.fields || {};
    this.params = { ...this.params, ...fields };
    this.callAPI();
  }
};
</script>

<style scoped>
.overview-header {
  color: #fff;
  border-radius: 10px;
  background: linear-gradient(120deg, #1565c0, #00897b);
}
.overview-description { max-width: 760px; opacity: 0.9; }
.overview-stat { height: 100%; border-radius: 10px; }
.overview-step { border-left: 4px solid #1976d2 !important; border-radius: 8px; }
.overview-item + .overview-item { border-top: 1px solid rgba(0, 0, 0, 0.08); }
</style>
